<script setup>
import { ref, computed, watch } from 'vue'
import { router, useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import Icon from '@/Components/UI/Icon.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import PhoneInput from '@/Components/UI/PhoneInput.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { centsToInput, inputToCents } from '@/composables/useMoneyInput.js'

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatMoney } = useCurrency()

const props = defineProps({
  customers:          Array,
  available_vehicles: Array,
  categories:         Array,
  offices:            { type: Array, default: () => [] },
  rate_plans:         { type: Array, default: () => [] },
  errors:             { type: Object, default: () => ({}) },
  prefill_customer:   { type: Object, default: null },
  defaults:           { type: Object, default: () => ({}) },
})

// Server errors — controller always returns a Hash (as_json)
const serverError = (key) => {
  const v = props.errors?.[key]
  return Array.isArray(v) ? v[0] : (v || null)
}

// ── Block 1 — Customer ────────────────────────────────────────────────────
const customerSearch       = ref(props.prefill_customer ? `${props.prefill_customer.first_name} ${props.prefill_customer.last_name}` : '')
const showCreateCustomer   = ref(false)
const selectedCustomer     = ref(props.prefill_customer || null)
const customerInputFocused = ref(false)

const filteredCustomers = computed(() => {
  if (!customerSearch.value) return props.customers?.slice(0, 5) || []
  const q = customerSearch.value.toLowerCase()
  return (props.customers || []).filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
    c.email?.toLowerCase().includes(q) ||
    c.phone?.includes(q)
  ).slice(0, 8)
})

const selectCustomer = (c) => {
  selectedCustomer.value       = c
  form.rental.customer_id      = c.id
  customerSearch.value         = `${c.first_name} ${c.last_name}`
}

// ── Block 2 — Category ───────────────────────────────────────────────────
const selectedCategory = ref(null)

// ── Block 3 — Plan + Start date ──────────────────────────────────────────
const selectedPlanId   = ref(null) // null = custom range
const applyDefaultRate = ref(false)

const plansForCategory = computed(() => {
  if (!selectedCategory.value) return []
  return (props.rate_plans || []).filter(p => {
    if (p.category) return p.category === selectedCategory.value
    // vehicle-specific: any assigned vehicle in this category?
    return (p.vehicle_ids || []).some(vid => {
      const v = (props.available_vehicles || []).find(x => x.id === vid)
      return v?.category === selectedCategory.value
    })
  })
})

const selectedPlan = computed(() =>
  plansForCategory.value.find(p => p.id === selectedPlanId.value)
)

const isoDateFromMs = (ms) => {
  const d = new Date(ms)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const onCategoryChange = (cat) => {
  selectedCategory.value = cat
  selectedPlanId.value     = null
  selectedVehicleId.value  = null
  form.rental.rate_plan_id = null
  form.rental.vehicle_id   = null
  form.rental.daily_rate_cents   = 0
  form.rental.deposit_paid_cents = 0
}

const onPlanChange = (planId) => {
  selectedPlanId.value = planId
  selectedVehicleId.value = null
  form.rental.vehicle_id  = null
  form.rental.deposit_paid_cents = 0

  if (planId) {
    const p = plansForCategory.value.find(x => x.id === planId)
    if (p) {
      form.rental.rate_plan_id     = p.id
      form.rental.daily_rate_cents = Math.floor(p.total_cents / p.duration_days)
      form.rental.expected_end_at  = isoDateFromMs(new Date(form.rental.start_at).getTime() + p.duration_days * 86400000)
      applyDefaultRate.value = false
    }
  } else {
    form.rental.rate_plan_id     = null
    form.rental.daily_rate_cents = 0
  }
}

const onCustomPriceInput = () => {
  if (applyDefaultRate.value) applyDefaultRate.value = false
}

const applyDefaultRateIfChecked = () => {
  if (!applyDefaultRate.value) return
  if (!selectedVehicle.value) return
  form.rental.daily_rate_cents = Number(selectedVehicle.value.daily_rate_cents) || 0
}

// ── Block 4 — Vehicle ─────────────────────────────────────────────────────
const includeMaintenance = ref(false)
const selectedVehicleId  = ref(null)

// Vehicle is available iff none of its bookings overlap the chosen [start, end).
// Overlap rule mirrors Rental#no_overlapping_rentals: start_a < end_b AND end_a > start_b.
const datesValid = computed(() => {
  const s = form.rental.start_at
  const e = form.rental.expected_end_at
  return Boolean(s && e && new Date(e) > new Date(s))
})

const isVehicleFreeForDates = (v) => {
  if (!datesValid.value) return true
  const s = new Date(form.rental.start_at).getTime()
  const e = new Date(form.rental.expected_end_at).getTime()
  return !(v.bookings || []).some(b => {
    const bs = new Date(b.start_at).getTime()
    const be = new Date(b.expected_end_at).getTime()
    return bs < e && be > s
  })
}

const filteredVehicles = computed(() => {
  if (!selectedCategory.value) return []
  let v = (props.available_vehicles || []).filter(x => x.category === selectedCategory.value)
  if (!includeMaintenance.value) v = v.filter(x => x.status !== 'maintenance')
  v = v.filter(isVehicleFreeForDates)
  if (selectedPlanId.value) {
    const p = plansForCategory.value.find(x => x.id === selectedPlanId.value)
    if (p && !p.category) {
      v = v.filter(x => (p.vehicle_ids || []).includes(x.id))
    }
  }
  return v
})

const selectVehicle = (v) => {
  selectedVehicleId.value         = v.id
  form.rental.vehicle_id          = v.id
  form.rental.deposit_paid_cents  = Number(v.deposit_amount_cents) || 0
  // Plan flow already set daily_rate_cents; Custom range with applyDefaultRate
  // is handled by the applyDefaultRateIfChecked watcher.
  if (selectedPlanId.value) return
  if (applyDefaultRate.value) form.rental.daily_rate_cents = Number(v.daily_rate_cents) || 0
}

const selectedVehicle = computed(() =>
  (props.available_vehicles || []).find(v => v.id === selectedVehicleId.value)
)

// Auto-fill rate when vehicle becomes available and tick is on
watch(() => selectedVehicleId.value, () => {
  applyDefaultRateIfChecked()
})

watch(applyDefaultRate, (v) => {
  if (v) applyDefaultRateIfChecked()
  else if (!selectedPlanId.value) form.rental.daily_rate_cents = 0
})

// ── Dates / defaults ──────────────────────────────────────────────────────
const now          = new Date()
const isoDate      = (d) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
const defaultStart = isoDate(now)
const defaultEnd   = isoDate(new Date(now.getTime() + 86400000))

const defaultOfficeId = (props.offices || []).find(o => o.is_default)?.id
                     ?? (props.offices || [])[0]?.id
                     ?? null

const form = useForm({
  rental: {
    customer_id:         props.prefill_customer?.id ?? null,
    vehicle_id:          null,
    office_id:           defaultOfficeId,
    rate_plan_id:        null,
    start_at:            defaultStart,
    expected_end_at:     defaultEnd,
    daily_rate_cents:    0,
    deposit_paid_cents:  0,
    notes:               '',
  },
  new_customer: {
    first_name:     '',
    last_name:      '',
    phone:          '',
    licence_number: '',
    licence_expiry: '',
  },
})

const durationDays = computed(() => {
  if (!form.rental.start_at || !form.rental.expected_end_at) return 1
  const diff = (new Date(form.rental.expected_end_at) - new Date(form.rental.start_at)) / 86400000
  return Math.max(Math.ceil(diff), 1)
})

const runningTotal = computed(() => durationDays.value * form.rental.daily_rate_cents)

const licenceExpiredWarning = computed(() => {
  if (!selectedCustomer.value?.licence_expiry) return false
  return new Date(selectedCustomer.value.licence_expiry) < new Date(form.rental.expected_end_at)
})

const acceptExpired = ref(false)

watch(customerSearch, (val) => {
  if (selectedCustomer.value && val !== `${selectedCustomer.value.first_name} ${selectedCustomer.value.last_name}`) {
    selectedCustomer.value      = null
    form.rental.customer_id     = null
  }
})

// Deselect the chosen vehicle when the dates change to a window where it's no
// longer free, so the operator can't submit a rental that would fail the
// server-side overlap validation.
watch(
  () => [form.rental.start_at, form.rental.expected_end_at],
  () => {
    if (!selectedVehicle.value) return
    if (!isVehicleFreeForDates(selectedVehicle.value)) {
      selectedVehicleId.value         = null
      form.rental.vehicle_id          = null
      form.rental.daily_rate_cents    = 0
      form.rental.deposit_paid_cents  = 0
    }
  },
)

function goBack() {
  if (typeof window !== "undefined" && window.history.length > 1) {
    window.history.back()
  } else {
    router.visit("/rentals")
  }
}

// ── Submit ────────────────────────────────────────────────────────────────
const submit = () => {
  if (licenceExpiredWarning.value && !acceptExpired.value) return
  form.transform(data => {
    const payload = { rental: { ...data.rental } }
    if (showCreateCustomer.value) payload.new_customer = data.new_customer
    if (props.defaults?.return_to) payload.return_to = props.defaults.return_to
    return payload
  }).post('/rentals', { replace: true })
}
</script>

<template>
  <PageShell :title="t('rental.create.title')">
    <div class="max-w-2xl mx-auto w-full">
      <button
        type="button"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors mb-4"
        :aria-label="t('common.back')"
        @click="goBack"
      >
        <Icon
          name="chev-l"
          :size="18"
        />
      </button>

      <form
        class="space-y-6"
        novalidate
        @submit.prevent="submit"
      >
        <!-- Block 1: Customer -->
        <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 space-y-4">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            1. {{ t('rental.create.block1') }}
          </h2>

          <div class="relative">
            <label
              for="customer-search"
              class="sr-only"
            >{{ t('rental.create.customerSearch') }}</label>
            <input
              id="customer-search"
              v-model="customerSearch"
              type="text"
              :placeholder="t('rental.create.customerSearch')"
              autocomplete="off"
              class="w-full px-4 py-2.5 rounded-full border bg-[color:var(--surface)] text-sm text-[color:var(--ink)] placeholder:text-[color:var(--muted)] focus:outline-2 focus:outline-[color:var(--accent)]"
              :class="serverError('customer') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
              :aria-describedby="serverError('customer') ? 'customer-err' : undefined"
              aria-autocomplete="list"
              aria-controls="customer-listbox"
              @focus="showCreateCustomer = false; customerInputFocused = true"
              @blur="customerInputFocused = false"
            >
            <!-- Dropdown results -->
            <ul
              v-if="customerInputFocused && !selectedCustomer"
              id="customer-listbox"
              class="absolute z-10 w-full mt-1 bg-[color:var(--surface)] border border-[color:var(--line)] rounded-[var(--radius-lg)] shadow-lg max-h-48 overflow-y-auto"
              role="listbox"
              :aria-label="t('rental.create.customerSearch')"
            >
              <li
                v-for="c in filteredCustomers"
                :key="c.id"
                class="px-4 py-2.5 text-sm cursor-pointer hover:bg-[color:var(--accentSoft)] flex justify-between"
                role="option"
                :aria-selected="form.rental.customer_id === c.id"
                @mousedown.prevent
                @click="selectCustomer(c)"
              >
                <span class="text-[color:var(--ink)]">{{ c.first_name }} {{ c.last_name }}</span>
                <span class="text-[color:var(--muted)]">{{ c.phone }}</span>
              </li>
              <li
                v-if="!filteredCustomers.length"
                class="px-4 py-2.5 text-sm text-[color:var(--muted)]"
                role="option"
                aria-disabled="true"
              >
                {{ t('rental.create.noCustomerMatch') }}
              </li>
            </ul>
            <p
              v-if="serverError('customer')"
              id="customer-err"
              class="mt-1 text-xs text-[color:var(--danger)]"
              role="alert"
            >
              {{ serverError('customer') }}
            </p>
          </div>

          <!-- Licence warning -->
          <div
            v-if="licenceExpiredWarning"
            class="p-3 bg-[color:var(--dangerSoft)] border border-[color:var(--danger)] rounded-[var(--radius-lg)] text-sm text-[color:var(--danger)]"
            role="alert"
          >
            &#9888; {{ t('rental.create.licenceExpiredWarning') }}
            <label class="flex items-center gap-2 mt-2 font-normal cursor-pointer">
              <input
                v-model="acceptExpired"
                type="checkbox"
                class="rounded"
              >
              {{ t('rental.create.acceptExpired') }}
            </label>
          </div>

          <!-- Inline create customer toggle -->
          <UiButton
            variant="ghost"
            size="sm"
            icon-left="plus"
            type="button"
            :aria-expanded="showCreateCustomer"
            @click="showCreateCustomer = !showCreateCustomer"
          >
            {{ t('rental.create.createCustomer') }}
          </UiButton>

          <div
            v-if="showCreateCustomer"
            class="grid grid-cols-2 gap-3 p-4 bg-[color:var(--surfaceAlt)] rounded-[var(--radius-lg)] border border-[color:var(--line)]"
          >
            <div>
              <label
                for="nc-first-name"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.newCustomer.firstName') }} *
              </label>
              <input
                id="nc-first-name"
                v-model="form.new_customer.first_name"
                type="text"
                required
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                :class="serverError('first_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
                :aria-describedby="serverError('first_name') ? 'nc-first-name-err' : undefined"
              >
              <p
                v-if="serverError('first_name')"
                id="nc-first-name-err"
                class="mt-1 text-xs text-[color:var(--danger)]"
                role="alert"
              >
                {{ serverError('first_name') }}
              </p>
            </div>
            <div>
              <label
                for="nc-last-name"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.newCustomer.lastName') }} *
              </label>
              <input
                id="nc-last-name"
                v-model="form.new_customer.last_name"
                type="text"
                required
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                :class="serverError('last_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
                :aria-describedby="serverError('last_name') ? 'nc-last-name-err' : undefined"
              >
              <p
                v-if="serverError('last_name')"
                id="nc-last-name-err"
                class="mt-1 text-xs text-[color:var(--danger)]"
                role="alert"
              >
                {{ serverError('last_name') }}
              </p>
            </div>
            <div>
              <label
                for="nc-phone"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.newCustomer.phone') }}
              </label>
              <PhoneInput
                id="nc-phone"
                v-model="form.new_customer.phone"
                :error="serverError('phone') || ''"
              />
            </div>
            <div>
              <label
                for="nc-licence"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.newCustomer.licenceNumber') }}
              </label>
              <input
                id="nc-licence"
                v-model="form.new_customer.licence_number"
                type="text"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
              >
            </div>
            <div class="col-span-2">
              <label
                for="nc-expiry"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.newCustomer.licenceExpiry') }}
              </label>
              <input
                id="nc-expiry"
                v-model="form.new_customer.licence_expiry"
                type="date"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
              >
            </div>
          </div>
        </section>

        <!-- Block 2: Type of vehicle -->
        <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 space-y-4">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            2. {{ t('rental.create.category') }}
          </h2>
          <div
            class="flex flex-wrap items-center gap-2"
            role="group"
            :aria-label="t('rental.create.category')"
          >
            <button
              v-for="cat in (categories || [])"
              :key="cat"
              type="button"
              class="px-3.5 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize"
              :style="selectedCategory === cat
                ? { backgroundColor: 'var(--accent)', color: 'var(--accentInk)', borderColor: 'var(--accent)' }
                : {}"
              :class="selectedCategory === cat
                ? ''
                : 'bg-[color:var(--surface)] text-[color:var(--inkSoft)] border-[color:var(--line)] hover:border-[color:var(--accent)]'"
              :aria-pressed="selectedCategory === cat"
              @click="onCategoryChange(cat)"
            >
              {{ cat }}
            </button>
          </div>
        </section>

        <!-- Block 3: Plan + Start date -->
        <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 space-y-4">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            3. {{ t('rental.create.plan') }}
          </h2>

          <p
            v-if="!selectedCategory"
            class="text-sm text-[color:var(--muted)]"
          >
            {{ t('rental.create.planPickerEmpty') }}
          </p>

          <template v-else>
            <div>
              <label
                for="rental-start"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.startDate') }} *
              </label>
              <input
                id="rental-start"
                v-model="form.rental.start_at"
                type="date"
                required
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                :aria-describedby="serverError('start_at') ? 'start-err' : undefined"
              >
              <p
                v-if="serverError('start_at')"
                id="start-err"
                class="mt-1 text-xs text-[color:var(--danger)]"
                role="alert"
              >
                {{ serverError('start_at') }}
              </p>
            </div>

            <div class="space-y-2">
              <p
                v-if="plansForCategory.length === 0"
                class="text-xs text-[color:var(--muted)]"
              >
                {{ t('rental.create.noPlansForCategory') }}
              </p>

              <button
                v-for="p in plansForCategory"
                :key="p.id"
                type="button"
                class="w-full text-left rounded-[var(--radius-md)] border-2 p-3 transition-all"
                :style="selectedPlanId === p.id
                  ? { borderColor: 'var(--accent)', backgroundColor: 'var(--accentSoft)' }
                  : {}"
                :class="selectedPlanId === p.id
                  ? ''
                  : 'border-[color:var(--line)] hover:border-[color:var(--accent)] bg-[color:var(--surface)]'"
                @click="onPlanChange(p.id)"
              >
                <div class="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <p class="fw-value text-sm text-[color:var(--ink)]">
                      {{ p.name }}
                    </p>
                    <p class="text-xs text-[color:var(--muted)]">
                      {{ p.duration_days }} {{ t('rental.create.days') }} · {{ formatMoney(p.total_cents) }}
                    </p>
                  </div>
                  <span
                    v-if="!p.category"
                    class="text-xs px-2 py-0.5 rounded-full"
                    :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
                  >{{ t('ratePlans.types.vehicleSpecific') }}</span>
                </div>
              </button>

              <!-- Custom range tile -->
              <button
                type="button"
                class="w-full text-left rounded-[var(--radius-md)] border-2 p-3 transition-all"
                :style="selectedPlanId === null
                  ? { borderColor: 'var(--accent)', backgroundColor: 'var(--accentSoft)' }
                  : {}"
                :class="selectedPlanId === null
                  ? ''
                  : 'border-[color:var(--line)] hover:border-[color:var(--accent)] bg-[color:var(--surface)]'"
                @click="onPlanChange(null)"
              >
                <p class="fw-value text-sm text-[color:var(--ink)]">
                  {{ t('rental.create.customRange') }}
                </p>
                <p class="text-xs text-[color:var(--muted)]">
                  {{ t('rental.create.customRangeHint') }}
                </p>
              </button>
            </div>

            <!-- Custom range inputs -->
            <div
              v-if="selectedPlanId === null"
              class="space-y-3 pt-2 border-t border-[color:var(--line)]"
            >
              <div>
                <label
                  for="rental-end"
                  class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
                >
                  {{ t('rental.create.expectedEnd') }} *
                </label>
                <input
                  id="rental-end"
                  v-model="form.rental.expected_end_at"
                  type="date"
                  required
                  class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                  :aria-describedby="serverError('expected_end_at') ? 'end-err' : undefined"
                >
                <p
                  v-if="serverError('expected_end_at')"
                  id="end-err"
                  class="mt-1 text-xs text-[color:var(--danger)]"
                  role="alert"
                >
                  {{ serverError('expected_end_at') }}
                </p>
              </div>
              <div>
                <label
                  for="rental-rate"
                  class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
                >
                  {{ t('rental.create.dailyRate') }}
                </label>
                <input
                  id="rental-rate"
                  :value="centsToInput(form.rental.daily_rate_cents)"
                  type="number"
                  min="0"
                  step="any"
                  class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                  @input="form.rental.daily_rate_cents = inputToCents($event.target.value); onCustomPriceInput()"
                >
                <label class="mt-2 inline-flex items-center gap-2 text-xs text-[color:var(--inkSoft)] cursor-pointer">
                  <input
                    v-model="applyDefaultRate"
                    type="checkbox"
                    class="rounded"
                  >
                  {{ t('rental.create.applyDefaultRate') }}
                </label>
              </div>
            </div>

            <!-- Preset plan: show computed return date -->
            <div
              v-else-if="selectedPlan"
              class="text-xs text-[color:var(--muted)] pt-2 border-t border-[color:var(--line)]"
            >
              {{ t('rental.create.returnsOn', { date: new Date(new Date(form.rental.start_at).getTime() + selectedPlan.duration_days * 86400000).toLocaleDateString() }) }}
            </div>
          </template>
        </section>

        <!-- Block 4: Vehicle (+ deposit + notes inline) -->
        <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 space-y-4">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            4. {{ t('rental.create.block3') }}
          </h2>

          <p
            v-if="!selectedCategory"
            class="text-sm text-[color:var(--muted)]"
          >
            {{ t('rental.create.pickCategoryFirst') }}
          </p>

          <template v-else>
            <!-- Include maintenance toggle -->
            <label class="flex items-center gap-1.5 text-xs text-[color:var(--muted)] cursor-pointer">
              <input
                v-model="includeMaintenance"
                type="checkbox"
                class="rounded"
              >
              {{ t('rental.create.includeMaintenance') }}
            </label>

            <!-- Vehicle grid -->
            <div
              class="grid grid-cols-2 md:grid-cols-3 gap-3"
              role="group"
              :aria-label="t('rental.create.block3')"
            >
              <button
                v-for="v in filteredVehicles"
                :key="v.id"
                type="button"
                class="relative text-left rounded-[var(--radius-lg)] border-2 p-3 transition-all"
                :style="selectedVehicleId === v.id
                  ? { borderColor: 'var(--accent)', backgroundColor: 'var(--accentSoft)' }
                  : {}"
                :class="selectedVehicleId === v.id
                  ? ''
                  : 'border-[color:var(--line)] hover:border-[color:var(--accent)] bg-[color:var(--surface)]'"
                :aria-pressed="selectedVehicleId === v.id"
                @click="selectVehicle(v)"
              >
                <div class="aspect-[4/3] bg-[color:var(--surfaceAlt)] rounded-[var(--radius-md)] mb-2 overflow-hidden">
                  <img
                    v-if="v.photo_url"
                    :src="v.photo_url"
                    :alt="`${v.make} ${v.model}`"
                    class="w-full h-full object-cover"
                  >
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center text-[color:var(--muted)]"
                    aria-hidden="true"
                  >
                    <svg
                      class="w-8 h-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    ><path d="M12 3C7 3 3 7 3 12s4 9 9 9 9-4 9-9-4-9-9-9z" /></svg>
                  </div>
                </div>
                <p class="font-semibold text-sm text-[color:var(--ink)] truncate">
                  {{ v.make }} {{ v.model }}
                </p>
                <p class="text-xs text-[color:var(--muted)] font-mono">
                  {{ v.registration }}
                </p>
                <p
                  class="text-sm fw-value tabular-nums mt-1"
                  style="color: var(--accent)"
                >
                  {{ formatMoney(v.daily_rate_cents) }}{{ t('rental.create.perDay') }}
                </p>
                <div
                  v-if="selectedVehicleId === v.id"
                  class="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                  style="background-color: var(--accent); color: var(--accentInk);"
                  aria-hidden="true"
                >
                  <Icon
                    name="check"
                    :size="12"
                    :stroke="3"
                  />
                </div>
              </button>
            </div>

            <p
              v-if="!filteredVehicles.length && datesValid"
              class="text-sm text-[color:var(--muted)] py-4 text-center"
            >
              {{ t('rental.create.noVehiclesForDates') }}
            </p>
            <p
              v-if="serverError('vehicle')"
              id="vehicle-err"
              class="text-xs text-[color:var(--danger)]"
              role="alert"
            >
              {{ serverError('vehicle') }}
            </p>

            <!-- Inline deposit + notes -->
            <div class="grid grid-cols-2 gap-4 pt-2 border-t border-[color:var(--line)]">
              <div>
                <label
                  for="rental-deposit"
                  class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
                >
                  {{ t('rental.create.deposit') }}
                </label>
                <input
                  id="rental-deposit"
                  :value="centsToInput(form.rental.deposit_paid_cents)"
                  type="number"
                  min="0"
                  step="any"
                  class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
                  @input="form.rental.deposit_paid_cents = inputToCents($event.target.value)"
                >
              </div>
            </div>
            <div>
              <label
                for="rental-notes"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.notes') }}
              </label>
              <textarea
                id="rental-notes"
                v-model="form.rental.notes"
                rows="2"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] resize-none focus:outline-2 focus:outline-[color:var(--accent)]"
              />
            </div>

            <div v-if="(offices || []).length >= 2">
              <label
                for="rental-office"
                class="block text-xs font-medium text-[color:var(--inkSoft)] mb-1"
              >
                {{ t('rental.create.office') }}
              </label>
              <select
                id="rental-office"
                v-model="form.rental.office_id"
                class="w-full px-3 py-2 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
              >
                <option
                  v-for="o in offices"
                  :key="o.id"
                  :value="o.id"
                >
                  {{ o.name }}
                </option>
              </select>
            </div>
          </template>
        </section>

        <!-- Block 5: Summary -->
        <section class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-5 space-y-4">
          <h2 class="text-base font-semibold text-[color:var(--ink)]">
            5. {{ t('rental.create.summary') }}
          </h2>
          <dl class="grid grid-cols-2 gap-y-2 text-sm">
            <dt class="text-[color:var(--muted)]">{{ t('rental.create.summaryDuration') }}</dt>
            <dd class="fw-value tabular-nums text-[color:var(--ink)]">
              {{ durationDays }} {{ t('rental.create.days') }} × {{ formatMoney(form.rental.daily_rate_cents) }}
            </dd>

            <dt class="text-[color:var(--muted)]">{{ t('rental.create.summaryRental') }}</dt>
            <dd class="fw-value tabular-nums text-[color:var(--ink)] text-base">{{ formatMoney(runningTotal) }}</dd>

            <dt class="text-[color:var(--muted)]">{{ t('rental.create.deposit') }}</dt>
            <dd class="fw-value tabular-nums text-[color:var(--ink)]">{{ formatMoney(form.rental.deposit_paid_cents) }}</dd>

            <dt class="text-[color:var(--ink)] font-semibold pt-2 border-t border-[color:var(--line)]">
              {{ t('rental.create.summaryTotalDue') }}
            </dt>
            <dd class="fw-value tabular-nums text-[color:var(--ink)] text-lg pt-2 border-t border-[color:var(--line)]">
              {{ formatMoney(runningTotal + Number(form.rental.deposit_paid_cents || 0)) }}
            </dd>
          </dl>
        </section>

        <StickyActionBar>
          <template #info>
            <div class="flex items-baseline gap-2">
              <span class="text-xs text-[color:var(--muted)] uppercase tracking-wide">{{ t('rental.create.total') }}</span>
              <span class="fw-value tabular-nums text-[color:var(--ink)] text-lg">{{ formatMoney(runningTotal) }}</span>
            </div>
          </template>
          <UiButton
            variant="primary"
            size="md"
            type="submit"
            :loading="form.processing"
            icon-left="plus"
          >
            {{ t('rental.create.submitButton') }}
          </UiButton>
        </StickyActionBar>
      </form>
    </div>
  </PageShell>
</template>
